import React, { lazy, Suspense } from "react";
import { Navigate, Route } from "react-router-dom";
import { TbsGuard } from "../components/AuthComponents/TbsGuard";
import PageLoader from "../components/PageLoader/PageLoader";
import { TBS_ENABLED } from "../config";

// The Tiendas Biai routes, in one file the coordinator owns.
//
// Two properties matter more than the routes themselves:
//
// 1. **They do not exist when `REACT_APP_TBS_ENABLED` is unset.** `tbsRoutes()` returns `null`, so
//    nothing is registered and `/gestion/*` falls through to the 404 page. Every storefront
//    deployment is in that state today, and "the route 404s" is a far better failure than "the route
//    loads and every call fails" — a half-configured build rendering a POS pointing at nothing is
//    worse than no POS.
//
// 2. **Every page is `lazy`.** Decision #15 chose one build with lazy routes for the MVP rather than
//    a second bundle. That is only true if the code actually splits: a static import would put the
//    whole management app into the storefront's main chunk, which shoppers on mobile data would pay
//    for and never use.

const ContactListPage = lazy(() => import("../pages/tbs/ContactListPage/ContactListPage"));
const ContactFormPage = lazy(() => import("../pages/tbs/ContactFormPage/ContactFormPage"));
const ContactDetailPage = lazy(() => import("../pages/tbs/ContactDetailPage/ContactDetailPage"));
const TbsItemListPage = lazy(() => import("../pages/tbs/TbsItemListPage/TbsItemListPage"));
const TbsItemFormPage = lazy(() => import("../pages/tbs/TbsItemFormPage/TbsItemFormPage"));
const InventoryLedgerPage = lazy(
  () => import("../pages/tbs/InventoryLedgerPage/InventoryLedgerPage"),
);
const InventoryTransferPage = lazy(
  () => import("../pages/tbs/InventoryTransferPage/InventoryTransferPage"),
);
const InventoryEntryPage = lazy(() => import("../pages/tbs/InventoryEntryPage/InventoryEntryPage"));
const PaymentListPage = lazy(() => import("../pages/tbs/PaymentListPage/PaymentListPage"));
const ShiftPage = lazy(() => import("../pages/tbs/ShiftPage/ShiftPage"));
const PosPage = lazy(() => import("../pages/tbs/PosPage/PosPage"));
const InvoiceListPage = lazy(() => import("../pages/tbs/InvoiceListPage/InvoiceListPage"));
const InvoiceDetailPage = lazy(() => import("../pages/tbs/InvoiceDetailPage/InvoiceDetailPage"));
const DailyResumePage = lazy(() => import("../pages/tbs/DailyResumePage/DailyResumePage"));
const ContabilidadPage = lazy(() => import("../pages/tbs/ContabilidadPage/ContabilidadPage"));
const DianPendingPage = lazy(() => import("../pages/tbs/DianPendingPage/DianPendingPage"));
const ConfigPage = lazy(() => import("../pages/tbs/ConfigPage/ConfigPage"));

/** Wraps a lazy page so the chunk's load has something to show. */
const withSuspense = (component: React.ComponentType) => {
  const Wrapped: React.FC = () => (
    <Suspense fallback={<PageLoader />}>{React.createElement(component)}</Suspense>
  );
  return Wrapped;
};

/**
 * Returns the routes as a fragment, or `null` when TBS is not part of this build.
 *
 * A function rather than an array because `<Routes>` reads its children directly — a `.map()` over
 * route descriptors works too, but this keeps each route visible as JSX in the file that owns it,
 * which is what the storefront's routes look like and what a reviewer can scan.
 */
export const tbsRoutes = () => {
  if (!TBS_ENABLED) return null;

  return (
    <>
      {/* Order matters: `/nuevo` is a literal that would otherwise be read as an `:id`. React
          Router v6 ranks static segments above dynamic ones so this is safe either way, but the
          same collision is the one that shipped as EB-544 on the server side, and listing it first
          costs nothing. */}
      <Route
        path="/gestion/contactos/nuevo"
        element={<TbsGuard requiredRole="cashier" component={withSuspense(ContactFormPage)} />}
      />
      <Route
        path="/gestion/contactos/:id/editar"
        element={<TbsGuard requiredRole="cashier" component={withSuspense(ContactFormPage)} />}
      />
      {/* The bare id is the **detail** view, and editing is a step further in. A list that opens
          straight into a form makes "look something up" and "change it" the same gesture, which is
          how a phone number gets edited by someone who only meant to read it. */}
      <Route
        path="/gestion/contactos/:id"
        element={<TbsGuard requiredRole="viewer" component={withSuspense(ContactDetailPage)} />}
      />
      <Route
        path="/gestion/contactos"
        element={<TbsGuard requiredRole="viewer" component={withSuspense(ContactListPage)} />}
      />

      {/* Products. `/nuevo` before `:id` for the same reason as above.

          The roles differ from contacts, and follow the frozen spec rather than the pattern: the
          catalog's writes are `x-role: admin` (`createItem`, `updateItem`, `deleteItem`), where a
          contact can be created by a cashier ringing up a new customer. A cashier who cannot change
          a price is the point — the till reads this catalog, and its prices are what the shop
          charges. Guarding the form at `cashier` would let anyone at the counter reprice the shop
          and only fail at the API, after the form was filled in. */}
      <Route
        path="/gestion/productos/nuevo"
        element={<TbsGuard requiredRole="admin" component={withSuspense(TbsItemFormPage)} />}
      />
      <Route
        path="/gestion/productos/:id/editar"
        element={<TbsGuard requiredRole="admin" component={withSuspense(TbsItemFormPage)} />}
      />
      <Route
        path="/gestion/productos"
        element={<TbsGuard requiredRole="viewer" component={withSuspense(TbsItemListPage)} />}
      />

      {/* Inventory. **`admin`, not `cashier`** — `createStockMovements` is `x-role: admin` in the
          frozen spec, and for a reason the guard should not soften: moving stock without selling
          anything is an accounting adjustment, and a cashier who finds a shelf short should tell
          somebody rather than correct the books mid-shift.

          One screen for all three reasons (`TBS-70`). The plan's §8.1 sketches an entry screen and
          an adjustments screen beside it, but `TBS-49` folded `adjustment` into the same endpoint as
          `initial` and `purchase` — two screens over one endpoint is two places to fix one bug. */}
      <Route
        path="/gestion/inventario/entrada"
        element={<TbsGuard requiredRole="admin" component={withSuspense(InventoryEntryPage)} />}
      />

      {/* Transfers (`TBS-73`). `admin` for the same reason the entry screen is: `createStockTransfer`
          is `x-role: admin`, and moving stock between two warehouses changes two ledgers and no
          money — an accounting act rather than a counter one. */}
      <Route
        path="/gestion/inventario/traslados"
        element={<TbsGuard requiredRole="admin" component={withSuspense(InventoryTransferPage)} />}
      />

      {/* The ledger is `member` to read (`TBS-71`): looking up why a number is what it is belongs to
          whoever is asking, and reading writes nothing. It is the wider audience of the two, which
          is why it — and not the entry form — is what `/gestion/inventario` lands on. */}
      <Route
        path="/gestion/inventario/movimientos"
        element={<TbsGuard requiredRole="viewer" component={withSuspense(InventoryLedgerPage)} />}
      />

      {/* A redirect rather than a screen. The plan's §8.1 gives this path a `StockListPage`, which
          `TBS-22` cancelled on 2026-08-13 — stock on screen is the product grid and the POS, not a
          fourth list of products. Sending it to the ledger keeps the typed URL working instead of
          404ing somebody who guessed the obvious path. **No guard here**: the route it lands on has
          one, and a second would only decide the same thing twice. */}
      <Route
        path="/gestion/inventario"
        element={<Navigate to="/gestion/inventario/movimientos" replace />}
      />

      {/* Payments. `viewer`, matching `listPayments`/`getPayment`'s `x-role: member` in the frozen
          spec — reading what came in today is what a shift handover is made of. **Voiding is
          `admin`**, and that is enforced on the server; here the button is simply not rendered for
          anyone else, so nobody is offered a door that does not open.

          No `/nuevo` route: taking a payment is the POS charge dialog (`POS-1`), which posts an
          inline payment with the sale. A standalone "record a payment" form is for settling an
          invoice later, and `INV-2` owns that screen. */}
      <Route
        path="/gestion/pagos"
        element={<TbsGuard requiredRole="viewer" component={withSuspense(PaymentListPage)} />}
      />

      {/* The till shift. `cashier`, matching all three of `openShift`, `getCurrentShift` and
          `closeShift` in the frozen spec — and unlike the read-only screens above, because this one
          is *only* actions. There is nothing here for a viewer to look at: without a shift the page
          is a form, and with one it is an arqueo. A single route rather than open/close pairs,
          since there is at most one open shift per terminal and the page can simply tell which
          state it is in. */}
      {/* **The counter lives at `/pos*`, the back office at `/gestion/*`** — plan §8.3.1 and
          §8.3.6 name both paths, and the split is real: one is a screen somebody stands in front
          of all day, the other is a set of screens somebody opens to look something up.

          SHF-1 first shipped the shift at `/gestion/turno`, which contradicted §8.3.6. Moved here
          rather than left to drift: it was a few hours old, `REACT_APP_TBS_ENABLED` is unset on
          every deployment, so nobody has a bookmark to break. */}
      <Route
        path="/pos/turno"
        element={<TbsGuard requiredRole="cashier" component={withSuspense(ShiftPage)} />}
      />

      {/* The POS. `cashier`, and the only screen in the system that is the whole job rather than a
          tool for part of it. */}
      <Route
        path="/pos"
        element={<TbsGuard requiredRole="cashier" component={withSuspense(PosPage)} />}
      />

      {/* Invoices. `viewer` on both, matching `listInvoices` and `getInvoice`'s `x-role: member` —
          reading what was sold is what a shift handover and a morning check are made of. **Voiding
          is `admin`** and is not a route: it is a button inside the detail, absent for anyone who
          cannot use it, and enforced on the server either way. */}
      {/* Before `:id`, because `pendientes-dian` is a literal that the dynamic segment would
          otherwise swallow — the same collision the Lambda router guards against, one layer up.
          React Router v6 ranks static above dynamic so this is safe either way; listing it first
          costs nothing and says what is going on. */}
      <Route
        path="/gestion/facturas/pendientes-dian"
        element={<TbsGuard requiredRole="viewer" component={withSuspense(DianPendingPage)} />}
      />
      <Route
        path="/gestion/facturas/:id"
        element={<TbsGuard requiredRole="viewer" component={withSuspense(InvoiceDetailPage)} />}
      />
      <Route
        path="/gestion/facturas"
        element={<TbsGuard requiredRole="viewer" component={withSuspense(InvoiceListPage)} />}
      />

      {/* The daily resume. `viewer`, matching the contract — and matching what `GET /dailyResume`
          requires today, which is a valid token and no particular role. This is the screen whose
          job is to prove the migration did not change the numbers, so who can open it should not
          change either. */}
      <Route
        path="/gestion/reportes/diario"
        element={<TbsGuard requiredRole="viewer" component={withSuspense(DailyResumePage)} />}
      />

      {/* The full contabilidad screen — the twin of the storefront's, so the two can be read side
          by side while the numbers are compared (`TBS-67`, phase 6 step 3).

          **`admin`, unlike the resume beside it, and that is why it is a separate route.** This one
          opens and edits the day's base, and `POST`/`PUT /v1/cash/daily-budget` are `x-role: admin`.
          Folding it into `/gestion/reportes/diario` would have put a section a viewer cannot use
          onto the one screen whose `viewer` audience was chosen deliberately — its job is to prove
          the migration did not change the numbers, and who may check that should stay wide. */}
      <Route
        path="/gestion/contabilidad"
        element={<TbsGuard requiredRole="admin" component={withSuspense(ContabilidadPage)} />}
      />

      {/* The configuration and its first-run checklist (CFG-2, `TBS-48`).

          **`admin`, and not because of the writes.** Every write on the four resources is admin
          server-side, but so is *reading* `GET /number-templates` — they carry the DIAN
          resolutions. A `viewer` guard would let a cashier onto a page whose numbering section is a
          403 and whose checklist could therefore never say whether the company can sell. */}
      <Route
        path="/gestion/configuracion"
        element={<TbsGuard requiredRole="admin" component={withSuspense(ConfigPage)} />}
      />
    </>
  );
};
